import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

//custom components
import { PrimaryButton, SecondaryButton, TextButton } from '../../Components/Buttons';
import { ConfirmModal, ManageVideoModal, SuccessModal } from '../../Components/Modal';
import { APIService } from '../../Service';
import { CustomTab, EmptyScreen, Loader } from '../../Components/Others';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomDataTable } from '../../Components/DataTable';
import { CustomSearchInput } from '../../Components/FormElements';

const TAB_TITLES = [
    { id: 1, label: 'Freshdesk' },
    { id: 2, label: 'Videos' },
    { id: 3, label: 'AMC' }
];

const ITEMS_PER_PAGE = 10;

const Config = () => {

    const { state } = useLocation();
    const navigate = useNavigate();
    const scrollRef = useRef(null);


    const [success, setSuccess] = useState(false);
    const [loader, setLoader] = useState(false);

    const [selectedTab, setSelectedTab] = useState(0);
    const [videoLoader, setVideoLoader] = useState(true);
    const [videoList, setVideoList] = useState([]);

    const [manageVideoModal, setManageVideoModal] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);


    const [filterList, setFilterList] = useState([]);
    const [filterLoader, setFilterLoader] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [amcList, setAmcList] = useState({});
    const [amcLoader, setAmcLoader] = useState(true);

    const [amcManageModal, setAmcManageModal] = useState(false);
    const [amcData, setAmcData] = useState(null);
    const [amcStatus, setAmcStatus] = useState(false);
    const [amcConfirmLoader, setAmcConfirmLoader] = useState(false);

    const [modalContent, setModalContent] = useState({
        title: "",
        description: "",
    });

    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        if (selectedTab === 2 || selectedTab === 3) {
            _getVideoList();
            _getFilterList();
        }

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, [selectedTab]);

    useEffect(() => {
        if (success === true) {
            const modal = new Modal(document.getElementById("success-modal"));
            modal.show();
        }
    }, [success]);

    useEffect(() => {
        if (manageVideoModal === true) {
            const modal = new Modal(document.getElementById("manage-video-modal"));
            modal.show();
        }
    }, [manageVideoModal]);

    useEffect(() => {
        if (amcManageModal === true) {
            const modal = new Modal(document.getElementById("confirm-modal"));
            modal.show();
        }
    }, [amcManageModal]);

    useEffect(() => {
        if (state !== null) {
            if (state?.tab_index) {
                setSelectedTab(state.tab_index);
            } else {
                setSelectedTab(1);
            }
        }
        else {
            setSelectedTab(1);
        }
    }, [state]);

    useEffect(() => {
        if (selectedTab === 3) {
            _getAmcList(1);
        }
    }, [selectedTab]);

    // handle tab
    const _handleTab = (tab_id) => {
        if (tab_id === 3) {
            setAmcLoader(true);
        }
        navigate('/config', {
            state: {
                tab_index: tab_id
            }
        });

        setVideoLoader(true);
    };

    const _handleSync = () => {
        setLoader(true);
        _fetchData();
    };

    const _handleVideoClick = (videoUrl = "") => {
        if (!videoUrl) return;
        window.open(videoUrl, '_blank');
    };

    const _getYoutubeEmbedUrl = (videoUrl = "") => {
        if (!videoUrl) return "";
        const match = videoUrl.match(
            /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^?&/]+)/
        );
        const videoId = match?.[1];
        return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "";
    };


    // handle table page change 
    const _handlePageChange = (page_num) => {
        setCurrentPage(page_num);
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }

        _getAmcList(page_num);
        setAmcLoader(true);
    };

    const _handleEnableDisable = (amc_data) => {
        setAmcData(amc_data);
        setAmcManageModal(true);

        setModalContent({
            title: amc_data?.amc_is_enabled ? "Disable AMC" : "Enable AMC",
            description: `Are you sure want to ${amc_data?.amc_is_enabled ? "disable" : "enable"} this AMC?`,
        });
    };

    const _onSearch = () => {
        setAmcLoader(true);
        _getAmcList(1, searchValue);
    };

    // API - Risk assessment
    const _fetchData = () => {
        let is_post = false;
        let url = 'support/refresh';
        let request = {}
        APIService(is_post, url, request).then((response) => {
            if (response?.status_code === 200) {
                setSuccess(true);
            } else {
                setSuccess(false);
                toast.dismiss();
                toast.error(response?.message || "Unable to sync FAQ data. Please try again.", {
                    type: "error",
                    className: "e-toast",
                });
            }
            setLoader(false);
        })
    };

    // API - Get video list
    const _getVideoList = () => {

        let is_post = false;
        let url = 'video/list-video-url';
        let request = {}
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setVideoList(Array.isArray(response?.data) ? response.data : []);
            } else {
                setVideoList([]);
            }
            setVideoLoader(false);
        })
    };

    // API - Get filter list
    const _getFilterList = () => {
        setFilterLoader(true);

        let is_post = false;
        let url = 'video/filter-data';
        let request = {}
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setFilterList(Array.isArray(response?.data?.video_type) ? response.data.video_type : []);
            } else {
                setFilterList([]);
            }
            setFilterLoader(false);
        })
    };

    // API - Get amc list
    const _getAmcList = (page_num = currentPage, search = searchValue) => {

        let is_post = true;
        let url = 'amc/list';
        let data = {
            "page_num": page_num,
            "page_size": ITEMS_PER_PAGE,
            "query": search || ""
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setAmcList(response.data);
            }
            else {
                setAmcList({});
            }
            setAmcLoader(false);
        });
    };

    // API - Get amc list
    const _enableDisableAmc = () => {
        setAmcConfirmLoader(true);
        let is_post = true;
        let url = 'amc/manage';
        let data = {
            "amc_id": amcData?.amc_id || null,
        };
        APIService(is_post, url, data).then((response) => {
            if (response?.status_code === 200) {
                setAmcStatus(true);
                setModalContent({
                    title: "Success!",
                    description: amcData?.amc_is_enabled ? "The AMC has been disabled successfully." : "The AMC has been enabled successfully.",
                });

            }
            else {
                setAmcStatus(false);

                toast.error(response?.message || "Unable to update AMC. Please try again.", {
                    type: "error",
                    className: "e-toast",
                });
            }
            setAmcConfirmLoader(false);
        });
    };


    const TABLE_COLUMN = [
        {
            name: 'AMC Name',
            cell: row =>
                <div className='d-flex align-items-center gap-2'>
                    <div
                        className='w-32px h-32px e-bg-white border-radius-8 padding-2px-all'>
                        <img src={row.amc_image}
                            className='object-fit-contain w-100 h-100'
                            alt={row.amc_image} />
                    </div>

                    <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-18px text-capitalize text-break'>
                        {row.amc_name}
                    </p>
                </div>,
            width: "540px",
        },
        {
            name: 'Action',
            cell: row =>
                <TextButton name={row.amc_is_enabled ? 'Disable' : 'Enable'}
                    onClick={() => {
                        _handleEnableDisable(row);
                    }} />,
            width: "120px",
        },
    ];

    return (
        <Fragment>
            <section className='e-section-height pb-5 e-bg-magnolia' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-80px-bottom'>
                    <h3 className='margin-32px-bottom e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Config
                    </h3>
                </div>

                <div className='padding-52px-lr margin--80px-top'>
                    <div className={`${selectedTab === 3 ? "padding-32px-tb" : "padding-32px-all"} e-bg-ghost-white border-radius-16  min-vh-50`}>

                        <div className={`${selectedTab === 3 ? "padding-32px-lr" : ""} d-flex align-items-center justify-content-between `}>
                            <CustomTab data={TAB_TITLES}
                                selectedTab={selectedTab}
                                handleClick={_handleTab} />


                            {selectedTab === 3 &&
                                <CustomSearchInput placeholder={"Search for AMC"}
                                    type="text"
                                    value={searchValue}
                                    onSearch={() => _onSearch()}
                                    onClear={() => { setSearchValue(""); _getAmcList(1, ""); setAmcLoader(true); }}
                                    onSearchClear={() => { setSearchValue(""); _getAmcList(1, ""); setAmcLoader(true); }}
                                    className={"w-340px"}
                                    onKeyPress={() => _onSearch()}
                                    handleChange={(e) => {
                                        setSearchValue(e);
                                        if (e.length === 0) {
                                            _getAmcList(1, e);
                                            setAmcLoader(true);
                                        }
                                    }} />
                            }
                        </div>


                        {selectedTab === 1 ?
                            <div className='row mb-3 pt-5'>
                                <div className='col-xl-10 col-lg-9 col-8'>
                                    <h5 className='e-font-18'>Freshdesk Data Sync</h5>
                                </div>
                                <div className='col-xl-2 col-lg-3 col-4 ps-0 text-end'>
                                    <PrimaryButton name={'Sync FAQ Data'}
                                        paddingBlock='padding-12px-tb padding-24px-lr'
                                        className='w-100 text-nowrap'
                                        loader={loader}
                                        onClick={_handleSync} />
                                </div>
                            </div>
                            :
                            selectedTab === 2 ? <div className='padding-32px-top '>
                                {videoLoader ?
                                    <Loader className="flex-column justify-content-center min-vh-50" />
                                    :
                                    <div className='row row-gap-4'>
                                        {videoList?.length !== 0 ?
                                            videoList.map((video, key) => {
                                                const thumbnail = _getYoutubeEmbedUrl(video?.video_url);
                                                return (
                                                    <div key={key} className='col-12 col-lg-6'>
                                                        <div
                                                            className='e-bg-pale-lavender-light border-radius-16 d-flex flex-column justify-content-center overflow-hidden'
                                                            style={{ height: "240px" }}
                                                        >
                                                            {thumbnail ?
                                                                <div className='cursor-pointer w-100 h-100 ' onClick={() => _handleVideoClick(video?.video_url)}>
                                                                    <img
                                                                        src={thumbnail}
                                                                        className='w-100 h-100'
                                                                        alt={video?.video_key || "Video"}
                                                                    />
                                                                </div>
                                                                :
                                                                <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-center'>
                                                                    No video
                                                                </p>
                                                            }

                                                        </div>

                                                        <div className='d-flex align-items-center justify-content-between pt-2'>

                                                            <p className=' mb-0 e-text-smoky-black e-alt-font e-font-18 e-font-weight-500 line-height-24px text-capitalize text-break'>
                                                                {video?.video_key}
                                                            </p>

                                                            <SecondaryButton
                                                                name={thumbnail ? "Edit" : "Add"}
                                                                paddingBlock='py-2 padding-24px-lr'
                                                                onClick={() => {
                                                                    setManageVideoModal(true);
                                                                    setSelectedVideo(video);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })
                                            :
                                            <EmptyScreen
                                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                                title='No Videos Found.'
                                            />
                                        }
                                    </div>
                                }
                            </div>
                                :
                                selectedTab === 3 ?
                                    <>
                                        {amcLoader ?
                                            <Loader className="flex-column justify-content-center min-vh-100" />
                                            :
                                            Object.keys(amcList).length !== 0 && amcList?.data?.length !== 0 ?
                                                <div ref={scrollRef} className='e-data-table-wrapper-default-updated pt-3'>
                                                    <CustomDataTable columns={TABLE_COLUMN}
                                                        data={amcList.data}
                                                        pagination={true}
                                                        paginationPerPage={ITEMS_PER_PAGE}
                                                        paginationDefaultPage={amcList.current_page}
                                                        paginationTotalRows={amcList.total_count}
                                                        lastPage={amcList.total_page}
                                                        onChangePage={_handlePageChange}
                                                    />
                                                </div>
                                                :
                                                <EmptyScreen
                                                    image={require('../../Assets/Image/NotFound/no-data.png')}
                                                    title='No AMC Found.'
                                                />
                                        }
                                    </>
                                    :
                                    null
                        }

                    </div>
                </div>
            </section>

            {success === true &&
                <SuccessModal title="Success!"
                    description={"Data fetched successfully"}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setSuccess(false);
                        }, 200);
                    }} />
            }

            {manageVideoModal === true &&
                <ManageVideoModal
                    data={selectedVideo}
                    options={filterList}
                    optionsLoader={filterLoader}
                    handleClose={() => {
                        setTimeout(() => {
                            setManageVideoModal(false);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        _getVideoList();
                        setVideoLoader(true);
                        setTimeout(() => {
                            setManageVideoModal(false);
                        }, 200);
                    }}
                />
            }



            {amcManageModal === true &&
                <ConfirmModal title={modalContent.title}
                    description={modalContent.description}
                    status={amcStatus}
                    loader={amcConfirmLoader}
                    handleClose={() => {
                        setTimeout(() => {
                            setAmcManageModal(false);
                            setAmcStatus(false);
                            setAmcData(null);
                            setModalContent({ title: "", description: "" });
                        }, 200);
                    }}
                    handleConfirm={() => {
                        _enableDisableAmc();
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setAmcManageModal(false);
                            setAmcStatus(false);
                            setAmcData(null);
                            setModalContent({ title: "", description: "" });

                            _getAmcList(1, searchValue);
                            setAmcLoader(true);
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default Config;
